import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Shapes, Sparkles, Loader2, Check, X, Download, Wand2, ChevronDown, ChevronRight, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useProject } from '@/context/ProjectContext';
import { toast } from 'sonner';
import ProviderBadge from '@/components/ProviderBadge';
import ReportButton from '@/components/ReportButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VARIANT_LABELS = [
  { key: 'primary', label: 'Primary' },
  { key: 'mono', label: 'Mono' },
  { key: 'reversed', label: 'Reversed', dark: true },
  { key: 'icon', label: 'Icon' },
  { key: 'favicon', label: 'Favicon' },
];

const STYLE_PREFERENCES = [
  'Wordmark',
  'Icon Mark',
  'Lettermark',
  'Emblem',
  'Abstract Mark',
  'Combination Mark',
  'No Preference',
];

const EMPTY_LOGO_BRIEF = {
  style_preference: '',
  style_references: '',
  must_avoid: '',
  colour_direction: '',
  additional_notes: '',
};

const LogoStudioPanel = () => {
  const { currentProject, setBrandKit } = useProject();

  // Existing state
  const [brief, setBrief] = useState('');
  const [concepts, setConcepts] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [variantLoadingId, setVariantLoadingId] = useState(null);

  // BP-01-B: Logo Brief
  const [briefPanelOpen, setBriefPanelOpen] = useState(false);
  const [logoBrief, setLogoBrief] = useState({ ...EMPTY_LOGO_BRIEF });
  const [briefSaving, setBriefSaving] = useState(false);
  const [briefSaved, setBriefSaved] = useState(false);

  const setLogoBriefField = (key, value) => {
    setLogoBrief((prev) => ({ ...prev, [key]: value }));
    setBriefSaved(false);
  };

  const fetchConcepts = async () => {
    if (!currentProject) return;
    try {
      setFetching(true);
      const res = await axios.get(`${API}/projects/${currentProject.id}/logo/concepts`);
      setConcepts(res.data);
    } catch (error) {
      console.error('Failed to fetch logo concepts:', error);
    } finally {
      setFetching(false);
    }
  };

  const fetchLogoBrief = async () => {
    if (!currentProject) return;
    try {
      const res = await axios.get(`${API}/projects/${currentProject.id}/logo-brief`);
      if (res.data) {
        setLogoBrief({ ...EMPTY_LOGO_BRIEF, ...res.data });
        setBriefSaved(true);
      }
    } catch {
      // No brief saved yet — start empty
    }
  };

  useEffect(() => {
    fetchConcepts();
    fetchLogoBrief();
    if (currentProject) {
      const parts = [currentProject.name, currentProject.description].filter(Boolean);
      setBrief(parts.join(', '));
    }
  }, [currentProject?.id]);

  const handleSaveBrief = async () => {
    try {
      setBriefSaving(true);
      await axios.post(`${API}/projects/${currentProject.id}/logo-brief`, logoBrief);
      setBriefSaved(true);
      toast.success('Logo brief saved');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Failed to save logo brief');
    } finally {
      setBriefSaving(false);
    }
  };

  const buildLogoBriefContext = () => {
    const parts = [
      logoBrief.style_preference && `Style: ${logoBrief.style_preference}`,
      logoBrief.style_references && `References: ${logoBrief.style_references}`,
      logoBrief.must_avoid && `Avoid: ${logoBrief.must_avoid}`,
      logoBrief.colour_direction && `Colour direction: ${logoBrief.colour_direction}`,
      logoBrief.additional_notes && `Notes: ${logoBrief.additional_notes}`,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join('. ') : null;
  };

  const handleGenerate = async () => {
    if (!brief.trim()) {
      toast.error('Describe the business first');
      return;
    }
    try {
      setGenerating(true);
      const payload = { brief };
      const ctx = buildLogoBriefContext();
      if (ctx) payload.logo_brief_context = ctx;
      const res = await axios.post(`${API}/projects/${currentProject.id}/logo/generate-concepts`, payload);
      setConcepts((prev) => [...res.data, ...prev]);
      toast.success(`Generated ${res.data.length} concept direction${res.data.length !== 1 ? 's' : ''}`);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Failed to generate logo concepts');
    } finally {
      setGenerating(false);
    }
  };

  const handleReject = async (conceptId) => {
    try {
      const res = await axios.patch(`${API}/projects/${currentProject.id}/logo/concepts/${conceptId}`, { status: 'rejected' });
      setConcepts((prev) => prev.map((c) => (c.id === conceptId ? res.data : c)));
    } catch (error) {
      toast.error('Failed to update concept');
    }
  };

  const handleGenerateVariants = async (conceptId) => {
    try {
      setVariantLoadingId(conceptId);
      const res = await axios.post(`${API}/projects/${currentProject.id}/logo/concepts/${conceptId}/variants`);
      setConcepts((prev) => prev.map((c) => (c.id === conceptId ? res.data : c)));
      const brandKitRes = await axios.get(`${API}/brand-kits/${currentProject.id}`);
      setBrandKit(brandKitRes.data);
      toast.success('Variants generated and saved to your Brand Kit');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Failed to generate variants');
    } finally {
      setVariantLoadingId(null);
    }
  };

  const visibleConcepts = concepts.filter((c) => c.status !== 'rejected');
  const hasBriefContent = Object.values(logoBrief).some((v) => v.trim());

  if (!currentProject) return null;

  return (
    <div className="h-full overflow-auto p-6" data-testid="logo-studio-panel">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="mb-6 flex items-start justify-between gap-4">
            <div>
              <h2 className="font-heading text-2xl text-foreground mb-2">Logo Studio</h2>
              <p className="text-muted-foreground">Generate concept directions, pick one, and get a full variant set saved to your Brand Kit</p>
            </div>
            <ReportButton section="logo" projectId={currentProject.id} />
          </div>

          {/* Intake */}
          <div className="glass rounded-2xl p-6 border border-border mb-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Shapes className="w-5 h-5 text-primary" />
              </div>
              <h3 className="font-heading text-lg text-foreground">Brief</h3>
            </div>

            <div className="space-y-2 mb-4">
              <Label className="text-foreground/80">Describe the business</Label>
              <textarea
                value={brief}
                onChange={(e) => setBrief(e.target.value)}
                placeholder="e.g., Blueprints, a premium branding and design agency for startups"
                rows={2}
                className="w-full rounded-md bg-black/[0.03] border border-border text-foreground placeholder:text-muted-foreground/70 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
                data-testid="logo-brief-input"
              />
            </div>

            {/* BP-01-B: Logo Brief (collapsible) */}
            <div className="border-t border-border pt-4 mb-4">
              <button
                type="button"
                onClick={() => setBriefPanelOpen((o) => !o)}
                className="flex items-center gap-2 w-full text-left mb-1"
              >
                {briefPanelOpen ? (
                  <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                )}
                <span className="font-mono text-xs uppercase tracking-wide lodge-accent">
                  Logo Brief (BP-01-B)
                </span>
                {hasBriefContent && (
                  <span className="ml-auto text-[11px] text-emerald-600 font-mono">saved</span>
                )}
              </button>

              {briefPanelOpen && (
                <div className="mt-3 space-y-3">
                  <div className="space-y-1">
                    <Label className="text-foreground/80">Style Preference</Label>
                    <select
                      value={logoBrief.style_preference}
                      onChange={(e) => setLogoBriefField('style_preference', e.target.value)}
                      className="w-full rounded-md bg-black/[0.03] border border-border text-foreground px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
                    >
                      <option value="">— select —</option>
                      {STYLE_PREFERENCES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-foreground/80">Style References</Label>
                    <textarea
                      value={logoBrief.style_references}
                      onChange={(e) => setLogoBriefField('style_references', e.target.value)}
                      placeholder="Brand names or URLs you admire"
                      rows={2}
                      className="w-full rounded-md bg-black/[0.03] border border-border text-foreground placeholder:text-muted-foreground/70 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
                    />
                  </div>

                  <div className="space-y-1">
                    <Label className="text-foreground/80">Must Avoid</Label>
                    <textarea
                      value={logoBrief.must_avoid}
                      onChange={(e) => setLogoBriefField('must_avoid', e.target.value)}
                      placeholder="Styles, symbols, or colours to exclude"
                      rows={2}
                      className="w-full rounded-md bg-black/[0.03] border border-border text-foreground placeholder:text-muted-foreground/70 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
                    />
                  </div>

                  <div className="space-y-1">
                    <Label className="text-foreground/80">Colour Direction</Label>
                    <input
                      type="text"
                      value={logoBrief.colour_direction}
                      onChange={(e) => setLogoBriefField('colour_direction', e.target.value)}
                      placeholder="Any colour instincts beyond the brand palette?"
                      className="w-full rounded-md bg-black/[0.03] border border-border text-foreground placeholder:text-muted-foreground/70 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
                    />
                  </div>

                  <div className="space-y-1">
                    <Label className="text-foreground/80">Additional Notes</Label>
                    <textarea
                      value={logoBrief.additional_notes}
                      onChange={(e) => setLogoBriefField('additional_notes', e.target.value)}
                      placeholder="Anything else the designer should know"
                      rows={2}
                      className="w-full rounded-md bg-black/[0.03] border border-border text-foreground placeholder:text-muted-foreground/70 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
                    />
                  </div>

                  <Button
                    size="sm"
                    onClick={handleSaveBrief}
                    disabled={briefSaving}
                    className="bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                  >
                    {briefSaving ? (
                      <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Saving…</>
                    ) : (
                      <><Save className="w-3.5 h-3.5 mr-1.5" />Save Brief</>
                    )}
                  </Button>

                  {briefSaved && hasBriefContent && (
                    <p className="text-xs text-muted-foreground italic">
                      Brief context will be included in the next generation request.
                    </p>
                  )}
                </div>
              )}
            </div>

            <Button
              onClick={handleGenerate}
              disabled={generating}
              className="bg-primary text-primary-foreground font-semibold hover:bg-primary/90 disabled:opacity-50"
              data-testid="logo-generate-btn"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating 4 directions... (can take a minute or two)
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate Concepts
                </>
              )}
            </Button>
          </div>

          {/* Concepts */}
          {fetching && concepts.length === 0 ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 text-primary animate-spin" />
            </div>
          ) : visibleConcepts.length === 0 ? (
            <div className="glass rounded-2xl p-10 border border-border text-center">
              <Shapes className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" />
              <p className="text-muted-foreground">No logo concepts yet. Describe your business above and generate some.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {visibleConcepts.map((concept) => (
                <div
                  key={concept.id}
                  className="glass rounded-2xl p-5 border border-border"
                  data-testid={`logo-concept-${concept.id}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-mono uppercase tracking-wide text-muted-foreground">{concept.direction}</span>
                    <div className="flex items-center gap-2">
                      <ProviderBadge provider={concept.generated_by} />
                      {concept.status === 'selected' && (
                        <span className="px-2 py-0.5 rounded-full text-[11px] bg-emerald-500/10 text-emerald-600 border border-emerald-500/30">
                          Saved to Brand Kit
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="aspect-square rounded-xl bg-black/[0.03] border border-border flex items-center justify-center overflow-hidden mb-4">
                    <img src={concept.image_url} alt={concept.direction} className="w-full h-full object-contain" />
                  </div>

                  {!concept.variants ? (
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleGenerateVariants(concept.id)}
                        disabled={variantLoadingId === concept.id}
                        className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                        data-testid={`logo-select-${concept.id}`}
                      >
                        {variantLoadingId === concept.id ? (
                          <>
                            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <Wand2 className="w-3.5 h-3.5 mr-1.5" />
                            Select &amp; Generate Variants
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleReject(concept.id)}
                        className="bg-black/[0.03] border-border text-foreground hover:bg-black/[0.06]"
                        data-testid={`logo-reject-${concept.id}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ) : (
                    <div>
                      <div className="grid grid-cols-5 gap-2 mb-2">
                        {VARIANT_LABELS.map(({ key, label, dark }) => (
                          <div key={key} className="text-center">
                            <div className={`aspect-square rounded-lg border border-border flex items-center justify-center overflow-hidden mb-1 ${dark ? 'bg-[#1B3A5C]' : 'bg-white'}`}>
                              {concept.variants[key] && (
                                <img src={concept.variants[key]} alt={label} className="w-full h-full object-contain p-1" />
                              )}
                            </div>
                            <span className="text-[10px] text-muted-foreground">{label}</span>
                          </div>
                        ))}
                      </div>
                      {concept.variants.svg && (
                        <a
                          href={`data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(concept.variants.svg)))}`}
                          download={`logo-${concept.direction.toLowerCase().replace(/\s+/g, '-')}.svg`}
                          className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline mt-1"
                          data-testid={`logo-svg-download-${concept.id}`}
                        >
                          <Download className="w-3 h-3" />
                          Download SVG
                        </a>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default LogoStudioPanel;
